import React, { useState, useContext, useEffect } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle, Shield, Search, FileText, TrendingUp, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { ModalContext } from '../App';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './Home.css';

const Home = () => {
  const { openModal } = useContext(ModalContext);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const services = [
    {
      icon: <Shield size={40} />,
      title: 'Trademark Registration',
      description: 'Protect your brand identity with our comprehensive trademark registration services.',
      link: '/trademark-registration'
    },
    {
      icon: <FileText size={40} />,
      title: 'Copyright Registration',
      description: 'Secure your creative works with professional copyright registration assistance.',
      link: '/copyright-registration'
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Company Formation',
      description: 'Start your business right with expert company formation and legal setup.',
      link: '/company-formation'
    },
    {
      icon: <Search size={40} />,
      title: 'Trademark Search',
      description: 'Comprehensive trademark searches to ensure your brand is unique and available.',
      link: '/trademark-search'
    },
    {
      icon: <Award size={40} />,
      title: 'Amazon Brand Registry',
      description: 'Protect your Amazon listings and unlock powerful brand tools.',
      link: '/amazon-brand-registry'
    }
  ];

  const features = [
    {
      icon: <Shield size={32} />,
      title: 'Legal Protection',
      description: 'Comprehensive legal protection for your brand and intellectual property with nationwide coverage.'
    },
    {
      icon: <Users size={32} />,
      title: 'Expert Team',
      description: 'Dedicated case managers and registered attorneys guiding you through every step.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Fast Processing',
      description: 'Quick turnaround times with same-day filing options available for urgent needs.'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Trademarks Registered', description: 'Successfully protected brands across all industries' },
    { number: '98%', label: 'Success Rate', description: 'Industry-leading approval rate for trademark applications' },
    { number: '24/7', label: 'Support Available', description: 'Round-the-clock assistance for all your legal needs' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'Tech Startup Founder',
      text: 'Brand Name made the trademark process incredibly simple. Their team was professional, responsive, and got my brand protected in record time.',
      image: 'https://i.pravatar.cc/150?img=5'
    },
    {
      name: 'Michael Chen',
      company: 'E-commerce Business Owner',
      text: 'The Amazon Brand Registry service was a game-changer for my business. Now I have full control over my listings and brand protection.',
      image: 'https://i.pravatar.cc/150?img=12'
    },
    {
      name: 'Emily Rodriguez',
      company: 'Creative Director',
      text: 'Copyright registration for my creative work was seamless. The team handled everything professionally and kept me informed throughout.',
      image: 'https://i.pravatar.cc/150?img=47'
    },
    {
      name: 'David Thompson',
      company: 'Small Business Owner',
      text: 'From company formation to trademark registration, they provided comprehensive support. Best decision for my business!',
      image: 'https://i.pravatar.cc/150?img=13'
    },
    {
      name: 'Lisa Martinez',
      company: 'Fashion Brand Owner',
      text: 'The comprehensive trademark search saved me from potential conflicts. Their attention to detail is outstanding.',
      image: 'https://i.pravatar.cc/150?img=9'
    }
  ];

  const faqs = [
    {
      question: 'How long does trademark registration take?',
      answer: 'The complete trademark registration process typically takes 8-12 months from filing to registration, depending on USPTO processing times and any potential office actions.'
    },
    {
      question: 'What is the difference between trademark and copyright?',
      answer: 'A trademark protects brand names, logos, and slogans that identify your business, while copyright protects original creative works like books, music, art, and software.'
    },
    {
      question: 'Do I need a trademark search before filing?',
      answer: 'Yes, we highly recommend a comprehensive trademark search to identify potential conflicts and improve your chances of successful registration.'
    },
    {
      question: 'Can I register a trademark myself?',
      answer: 'While you can file yourself, working with professionals ensures proper classification, reduces rejection risk, and saves time navigating complex legal requirements.'
    },
    {
      question: 'What happens after my trademark is registered?',
      answer: 'Once registered, your trademark is protected nationwide. You must maintain it by filing renewals and monitoring for potential infringements.'
    }
  ];

  const [openFaq, setOpenFaq] = React.useState(null);

  return (
    <div className="home-page">
      <HeroSection
        eyebrow="PROTECT YOUR BRAND TODAY"
        title="Secure Your Business Identity"
        description="Professional trademark registration services starting at just $45. Fast, reliable, and trusted by thousands of businesses across America."
        ctaText="Get Started Now"
        ctaLink="#pricing"
        backgroundImage="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
        showForm={true}
      />

      {/* Why You Need This Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Why Trademark Protection is Essential</h2>
          <p className="section-subtitle">
            In today's competitive marketplace, protecting your brand identity is not just important-it's critical. 
            Without proper trademark protection, your business name, logo, and reputation are vulnerable to infringement, 
            imitation, and legal disputes that can cost you everything you've built.
          </p>
          
          {isMobile ? (
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              className="features-mobile-swiper"
            >
              {features.map((feature, index) => (
                <SwiperSlide key={index}>
                  <div className="feature-card">
                    <div className="feature-icon">{feature.icon}</div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How We Work Section */}
      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our streamlined three-step process makes protecting your brand simple and stress-free. 
            From initial consultation to final registration, we handle everything so you can focus on growing your business.
          </p>
          
          {isMobile ? (
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              className="process-mobile-swiper"
            >
              <SwiperSlide>
                <div className="process-step">
                  <div className="step-number">01</div>
                  <h3 className="step-title">Information Collection</h3>
                  <p className="step-description">
                    Share your brand details through our simple online form. We'll review your information 
                    and conduct an initial assessment to ensure eligibility.
                  </p>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="process-step">
                  <div className="step-number">02</div>
                  <h3 className="step-title">Research & Search Report</h3>
                  <p className="step-description">
                    Our experts conduct comprehensive searches to identify potential conflicts and ensure 
                    your trademark is unique and registrable.
                  </p>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="process-step">
                  <div className="step-number">03</div>
                  <h3 className="step-title">Application & Submission</h3>
                  <p className="step-description">
                    We prepare and file your complete application with the USPTO, monitoring progress 
                    and handling any responses needed until registration.
                  </p>
                </div>
              </SwiperSlide>
            </Swiper>
          ) : (
            <div className="process-grid">
              <div className="process-step">
                <div className="step-number">01</div>
                <h3 className="step-title">Information Collection</h3>
                <p className="step-description">
                  Share your brand details through our simple online form. We'll review your information 
                  and conduct an initial assessment to ensure eligibility.
                </p>
              </div>
              <div className="process-step">
                <div className="step-number">02</div>
                <h3 className="step-title">Research & Search Report</h3>
                <p className="step-description">
                  Our experts conduct comprehensive searches to identify potential conflicts and ensure 
                  your trademark is unique and registrable.
                </p>
              </div>
              <div className="process-step">
                <div className="step-number">03</div>
                <h3 className="step-title">Application & Submission</h3>
                <p className="step-description">
                  We prepare and file your complete application with the USPTO, monitoring progress 
                  and handling any responses needed until registration.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Services Section with Background and Continuous Slider */}
      <section className="section services-section-enhanced">
        <div className="services-background-overlay"></div>
        <div className="container">
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">
            Comprehensive intellectual property protection services tailored to your business needs.
          </p>
          <div className="services-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 25,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
              }}
              className="services-swiper"
            >
              {services.map((service, index) => (
                <SwiperSlide key={index}>
                  <a href={service.link} className="service-card-enhanced">
                    <div className="service-icon-themed">
                      {service.icon}
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">{service.description}</p>
                    <span className="service-link">Learn More →</span>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section section-light">
        <div className="container">
          <h2 className="section-title">Transparent Pricing Plans</h2>
          <p className="section-subtitle">
            Choose the package that best fits your needs. All plans include expert support and professional service.
          </p>
          <div className="pricing-grid">
            <PricingCard
              name="Basic"
              price="45"
              features={[
                'Case Review',
                'Case Filing',
                'Basic Search',
                'State E-Filing',
                'Trademark Secured'
              ]}
              onGetStarted={(packageName) => openModal(`Trademark Registration - ${packageName}`)}
            />
            <PricingCard
              name="Deluxe"
              price="145"
              features={[
                'Case Review',
                'Case Filing',
                'Basic Search',
                'State E-Filing',
                'Trademark Secured',
                'Direct Hit Search',
                'Dedicated Case Manager'
              ]}
              isPopular={true}
              onGetStarted={(packageName) => openModal(`Trademark Registration - ${packageName}`)}
            />
            <PricingCard
              name="Premium"
              price="299"
              features={[
                'Case Review (by a TM Expert)',
                'Basic Free Trademark Search',
                'Trademark Monitoring Services (3 Months)',
                'Trademark Secured',
                'Refusal Risk Meter',
                'Case filing service with complete paperwork'
              ]}
              onGetStarted={(packageName) => openModal(`Trademark Registration - ${packageName}`)}
            />
            <PricingCard
              name="Elite"
              price="599"
              features={[
                'Case Review & Filing (U.S Registered IP Attorney)',
                'Assigned Case Filing Officer',
                'Private Registration',
                'Trademark Secured',
                'Federal Search & Clearance Report (All States)',
                'Federal E-Filing (USPTO)',
                'Cease & Desist Letter',
                'Same Day Filing',
                'Trademark Monitoring (1 Year)',
                'Direct Hit Search',
                'Dedicated Case Manager',
                '24/7 Secure Online Access',
                '100% Approval Guarantee'
              ]}
              onGetStarted={(packageName) => openModal(`Trademark Registration - ${packageName}`)}
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <p className="section-subtitle">
            We're not just another legal services provider. We're your dedicated partner in protecting and growing your brand.
          </p>
          <div className="why-choose-grid">
            <div className="why-choose-card">
              <h3>Unmatched Expertise</h3>
              <p>
                Our team of registered attorneys and trademark specialists brings decades of combined experience 
                in intellectual property law, ensuring your brand receives the highest level of professional care.
              </p>
            </div>
            <div className="why-choose-card">
              <h3>Affordable Excellence</h3>
              <p>
                Quality legal services shouldn't break the bank. We offer competitive pricing without 
                compromising on service quality, making brand protection accessible to businesses of all sizes.
              </p>
            </div>
            <div className="why-choose-card">
              <h3>Comprehensive Support</h3>
              <p>
                From initial consultation to post-registration monitoring, we provide end-to-end support. 
                Our dedicated case managers are available 24/7 to address your concerns and guide you through every step.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section stats-section">
        <div className="container">
          <h2 className="section-title">Trusted by Thousands</h2>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
                <p className="stat-description">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
          <p className="section-subtitle">
            Don't just take our word for it. Here's what our satisfied clients have to say about our services.
          </p>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 25,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Get answers to common questions about our trademark and legal services.
          </p>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Protect Your Brand?</h2>
            <p className="cta-description">
              Join thousands of satisfied clients who have secured their business identity with Unity Trademark. 
              Get started today for just $45 and take the first step towards comprehensive brand protection.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Trademark Registration')}
            >
              Start Your Trademark Registration
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
